import React, {
    forwardRef,
    ReactNode,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { dateFormatZurich } from "utils/datetime";
import AntDatePicker from "./origin";
import { moment, momentWithCeilMinutes } from "../../utils/moment";
import FormGroup from "../FormGroup";
import FormControlMessage from "../FormControlMessage";
import Label from "../Label";
import Icon from "../Icon";
import { getClassNames } from "../../utils";
import {
    defaultDatePickerLocale,
    KEY_CODE_ENTER,
    KEY_CODE_TAB,
} from "./constants";
import { datePickerLocalePropTypes } from "./propTypes";
import withTheme from "../../hocs/withTheme";
import "./DatePicker.css";

type DatePickerProps = {
    use12Hours?: boolean;
    label?: string;
    labelInfobox?: boolean;
    placeholder?: string;
    defaultValue?: string;
    value?: string | typeof Date | typeof moment;
    disabled?: boolean;
    required?: boolean;
    error?: string | boolean | undefined;
    labels?: datePickerLocalePropTypes;
    minuteStep?: number;
    children?: ReactNode;
    format?: string;
    onChange?: (date: any, dateString: any) => void;
    dropdownClassName?: string;
    helpText?: boolean | string;
    showTime?: boolean;
    innerRef?: React.MutableRefObject<null> | undefined;
    labelInside?: string;
};

function DatePicker(props : DatePickerProps) {
    const {
        use12Hours = false,
        label = null,
        labelInfobox = false,
        placeholder = null,
        defaultValue = undefined,
        value = null,
        disabled = false,
        required = false,
        error = undefined,
        minuteStep = 5,
        children = null,
        format = dateFormatZurich,
        onChange = () => {},
        dropdownClassName = undefined,
        labels = defaultDatePickerLocale,
        helpText = undefined,
        showTime = false,
        innerRef = undefined,
        labelInside = null,
        ...rest
    } = props;
    const initEmptyValue = () =>
        showTime === true ? momentWithCeilMinutes() : moment();
    const [valueInput, setValueInput] = useState(
        defaultValue
            ? moment(moment(defaultValue).format(format), format)
            : null
    );
    const defaultValueInput = useMemo(
        () =>
            defaultValue
                ? moment(moment(defaultValue).format(format), format)
                : initEmptyValue(),
        [defaultValue, format]
    );
    const inputRef = useRef(null);

    const onChangeDate: (date: any, dateString?: any) => void = (
        date,
        dateString
    ) => {
        setValueInput(date);
        onChange(date, dateString);
    };

    useEffect(() => {
        if (value === null) setValueInput(null);
        else if (value && typeof value === "string") {
            setValueInput(moment(value, format));
        } else if(value)  {
            setValueInput(value as typeof moment);
        }
    }, [value]);

    const dropdownClass = getClassNames(dropdownClassName, "datepicker");

    const formGroupClass = getClassNames("datepicker-form-group", {
        "with-label-inside": labelInside,
        "with-label": label,
    });

    return (
        <FormGroup disabled={disabled} error={error} className={formGroupClass}>
            {label && (
                <Label
                    htmlFor={label}
                    label={label}
                    labelInfobox={labelInfobox}
                    required={required}
                    helpText={helpText}
                />
            )}
            <AntDatePicker
                {...rest}
                showTime={showTime}
                dropdownAlign={{ offset: [0, 13] }}
                use12Hours={use12Hours}
                locale={labels}
                allowClear={false}
                id={label}
                ref={innerRef || inputRef}
                value={valueInput}
                format={format}
                className={error ? "ant-input_invalid" : null}
                dropdownClassName={dropdownClass}
                suffixIcon={
                    <div>
                        <Icon type="calendar" />
                    </div>
                }
                disabled={disabled}
                required={required}
                onChange={onChangeDate}
                defaultValue={defaultValueInput}
                onKeyDown={(e) => {
                    if (
                        e?.keyCode === KEY_CODE_ENTER ||
                        e.keyCode === KEY_CODE_TAB
                    ) {
                        const formattedDate = moment(
                            `${e?.target?.value}`,
                            format
                        );
                        if (formattedDate?.isValid()) {
                            setValueInput(formattedDate);
                            onChangeDate(formattedDate);
                        }
                    }
                }}
            />
            {labelInside && <span className="label-inside">{labelInside}</span>}
            {error && <FormControlMessage>{error}</FormControlMessage>}

            {children && <p>{children}</p>}
        </FormGroup>
    );
}

export default forwardRef((props, ref) =>
    withTheme(DatePicker)({ ...props, innerRef: ref })
);
